import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import TableLoader from "$components/table/table-loader.js";
import ToggleSwitch from "$components/toggle-switch";
import { usePaginatedStoreWrapper } from "$utils/hooks/usePaginatedStore.js";
import {
    InitiativeOpportunitiesStore,
    fetchInitiativeOpportunities,
    reset,
    setFilters,
    setHiddenColumns,
    setPageIndex,
    setPagingInfo,
    setPerPage,
    setSort,
    setOpportunityStatus,
    setUserFlagged,
} from "$stores/initiativeOpportunities";
import { adjustShortlistCount } from "$stores/initiative-details";
import { buildDefaultColumns } from "./libs/curate-columns";
import { initiativesApi } from "../api";
import OpportunityPanel from "./OpportunityPanel";

const mapToTableData = (r) => ({ key: r.projectKey, ...r });

const CurateTab = () => {
    const { id: initiativeId } = useParams();
    const [selectedOpp, setSelectedOpp] = useState(null);
    const [showDismissed, setShowDismissed] = useState(false);

    const storeOptions = useMemo(
        () => ({
            fetchList: fetchInitiativeOpportunities,
            reset,
            setPagingInfo,
            suppressGlobalFilters: true,
            overrideGlobalFilters: {
                initiative: [initiativeId],
            },
        }),
        [initiativeId]
    );

    const { initiativeOpportunities, isLoading, isSortAsc, pageCount, pageIndex, pageSize, sort, filters, globalFilterRanges, hiddenColumns } =
        usePaginatedStoreWrapper(InitiativeOpportunitiesStore, storeOptions);

    const handleShortlist = useCallback(
        async (projectKey) => {
            const row = InitiativeOpportunitiesStore.getRawState().initiativeOpportunities.find(
                (r) => r.projectKey === projectKey
            );
            if (!row) return;
            const snapshot = JSON.stringify({
                clientName: row.clientName,
                serviceLine: row.serviceLine,
                industry: row.industry,
                probability: row.probability,
                averageRevenue: row.averageRevenue,
                crmName: row.crmName,
            });
            const prevStatus = row.opportunityStatus;
            setOpportunityStatus(projectKey, "shortlisted");
            adjustShortlistCount(1);
            try {
                await initiativesApi.shortlist(initiativeId, projectKey, snapshot);
            } catch {
                setOpportunityStatus(projectKey, prevStatus);
                adjustShortlistCount(-1);
            }
        },
        [initiativeId]
    );

    const handleRemoveShortlist = useCallback(
        async (projectKey) => {
            const row = InitiativeOpportunitiesStore.getRawState().initiativeOpportunities.find(
                (r) => r.projectKey === projectKey
            );
            const prevStatus = row?.opportunityStatus;
            setOpportunityStatus(projectKey, "active");
            adjustShortlistCount(-1);
            try {
                await initiativesApi.revert(initiativeId, projectKey);
            } catch {
                setOpportunityStatus(projectKey, prevStatus);
                adjustShortlistCount(1);
            }
        },
        [initiativeId]
    );

    const handleDismiss = useCallback(
        async (projectKey, dismissReasonId) => {
            const row = InitiativeOpportunitiesStore.getRawState().initiativeOpportunities.find(
                (r) => r.projectKey === projectKey
            );
            const prevStatus = row?.opportunityStatus;
            const wasShortlisted = prevStatus === "shortlisted";
            setOpportunityStatus(projectKey, "dismissed", dismissReasonId);
            if (wasShortlisted) adjustShortlistCount(-1);
            try {
                await initiativesApi.dismiss(initiativeId, projectKey, dismissReasonId);
            } catch {
                setOpportunityStatus(projectKey, prevStatus);
                if (wasShortlisted) adjustShortlistCount(1);
            }
        },
        [initiativeId]
    );

    const handleUndismiss = useCallback(
        async (projectKey) => {
            const row = InitiativeOpportunitiesStore.getRawState().initiativeOpportunities.find(
                (r) => r.projectKey === projectKey
            );
            const prevStatus = row?.opportunityStatus;
            setOpportunityStatus(projectKey, "active");
            try {
                await initiativesApi.revert(initiativeId, projectKey);
            } catch {
                setOpportunityStatus(projectKey, prevStatus);
            }
        },
        [initiativeId]
    );

    const handleFlag = useCallback(
        async (projectKey) => {
            setUserFlagged(projectKey);
            const row = InitiativeOpportunitiesStore.getRawState().initiativeOpportunities.find(
                (r) => r.projectKey === projectKey
            );
            const prevStatus = row?.opportunityStatus;
            const wasShortlisted = prevStatus === "shortlisted";
            setOpportunityStatus(projectKey, "dismissed");
            if (wasShortlisted) adjustShortlistCount(-1);
            try {
                await initiativesApi.dismiss(initiativeId, projectKey, null);
            } catch {
                setOpportunityStatus(projectKey, prevStatus);
                if (wasShortlisted) adjustShortlistCount(1);
            }
        },
        [initiativeId]
    );

    const rowClick = useCallback((row) => {
        setSelectedOpp(row);
    }, []);

    const columns = useMemo(
        () =>
            buildDefaultColumns({
                onShortlist: handleShortlist,
                onRemoveShortlist: handleRemoveShortlist,
                onDismiss: handleDismiss,
                onUndismiss: handleUndismiss,
                onFlagged: handleFlag,
                globalFilterRanges,
            }),
        [handleShortlist, handleRemoveShortlist, handleDismiss, handleUndismiss, handleFlag, globalFilterRanges]
    );

    const allData = useMemo(() => (initiativeOpportunities || []).map(mapToTableData), [initiativeOpportunities]);
    const data = useMemo(
        () => showDismissed ? allData : allData.filter((r) => r.opportunityStatus !== "dismissed"),
        [allData, showDismissed]
    );

    const dismissedCount = useMemo(
        () => allData.filter((r) => r.opportunityStatus === "dismissed").length,
        [allData]
    );

    return (
        <div className="curate-tab">
            <div className="curate-tab__toolbar">
                <ToggleSwitch
                    options={[
                        { label: "Hide Dismissed", value: false },
                        { label: "Show Dismissed", value: true },
                    ]}
                    selectedIndex={showDismissed ? 1 : 0}
                    updateField={(value) => setShowDismissed(value)}
                    analyticsLabel="Show Dismissed Toggle"
                />
                {dismissedCount > 0 && <span className="txt -small -light">({dismissedCount})</span>}
            </div>
            <TableLoader
                allowFilter={true}
                columns={columns}
                containerClass="curate-list"
                data={data}
                filters={filters}
                hiddenColumns={hiddenColumns}
                isLoading={isLoading}
                isSortAsc={isSortAsc}
                onRowClick={rowClick}
                pageIndex={pageIndex}
                pageSize={pageSize}
                pageCount={pageCount}
                setPageIndex={setPageIndex}
                setFilters={setFilters}
                setHiddenColumns={setHiddenColumns}
                setPagingInfo={setPagingInfo}
                setPerPage={setPerPage}
                setSort={setSort}
                sort={sort}
            />
            <OpportunityPanel
                opportunity={selectedOpp}
                onClose={() => setSelectedOpp(null)}
                onFlagged={handleFlag}
            />
        </div>
    );
};

export default CurateTab;
